import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useCompetition } from './CompetitionContext';
import { useAuth } from './auth/AuthContext';
import { subscribeToRealtime } from './realtimeHub';
import { MeetingSwitcher } from './MeetingSwitcher';
import '../css/app-shell.css';

function groupLinks({ isAdmin, isCommentary, isRegistration, isSponsorAdmin }) {
  const canAccessRacing = isAdmin || isRegistration || isCommentary;
  const canAccessRegistrationSection = isAdmin || isRegistration;
  const canAccessSettingsSection = isAdmin || isSponsorAdmin;

  return [
    {
      title: 'Public',
      links: [
        { to: '/live-timetable', label: 'Live Timetable', exact: true },
        { to: '/draw', label: 'Draw', exact: true }
      ]
    },
    canAccessRacing
      ? {
        title: 'Racing',
        links: [
          { to: '/racing-dashboard', label: 'Racing Dashboard' },
          ...(isAdmin || isCommentary ? [{ to: '/commentary', label: 'Commentary View' }] : [])
        ]
      }
      : null,
    canAccessRegistrationSection
      ? {
        title: 'Registration',
        links: [
          { to: '/registration-desk', label: 'Registration Desk' },
          { to: '/club-summary', label: 'Club Summary' }
        ]
      }
      : null,
    canAccessSettingsSection
      ? {
        title: 'Settings',
        links: [
          { to: '/competition-admin', label: 'Competition Admin' },
          { to: '/entry-admin', label: 'Entry Admin' },
          { to: '/events-admin', label: 'Events Admin' },
          { to: '/banding-helper', label: 'Banding Helper' },
          { to: '/victor-ludorum', label: 'Victor Ludorum' },
          { to: '/system-settings', label: 'System Settings' }
        ].filter(item => {
          if (item.to === '/events-admin') return isAdmin || isSponsorAdmin;
          return isAdmin;
        })
      }
      : null
  ].filter(Boolean);
}

const MEETING_SCOPED_BASE_PATHS = new Set([
  'live-timetable', 'draw', 'draw-export-pdf', 'racecards', 'race-cards',
  'competition-admin', 'entry-admin', 'events-admin', 'banding-helper', 'victor-ludorum',
  'system-settings', 'registration-desk', 'racing-dashboard',
  'commentary', 'club-summary'
]);

function SidebarToggleIcon({ collapsed }) {
  return (
    <svg className="app-sidebar-toggle-svg" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <rect x="3" y="4" width="2" height="12" rx="1" />
      {collapsed ? (
        <path d="M9 10l4-4v8l-4-4z" />
      ) : (
        <path d="M11 10L7 6v8l4-4z" />
      )}
    </svg>
  );
}

export function AppShell({ children }) {
  const DEFAULT_RESULT_TITLE_FLASH_SECONDS = 20;
  const configuredResultTitleFlashSeconds = Number.parseInt(process.env.REACT_APP_RESULT_TITLE_FLASH_SECONDS || '', 10);
  const resultTitleFlashDurationMs = ((Number.isFinite(configuredResultTitleFlashSeconds) && configuredResultTitleFlashSeconds > 0)
    ? configuredResultTitleFlashSeconds
    : DEFAULT_RESULT_TITLE_FLASH_SECONDS) * 1000;

  const [title, setTitle] = useState('regattamanager.co.uk');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('app-sidebar-collapsed') === 'true';
    } catch (_error) {
      return false;
    }
  });
  const { competitionName } = useCompetition();
  const { user, isAdmin, isCommentary, isRegistration, isSponsorAdmin, signOut, getToken } = useAuth();
  const location = useLocation();
  const titleFlashIntervalRef = useRef(null);
  const titleFlashTimeoutRef = useRef(null);
  const titleFlashBaseTitleRef = useRef('regatta');

  const getMeetingScopedPage = useCallback((pathname) => {
    const segments = String(pathname || '').split('/').filter(Boolean);
    if (segments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(segments[1])) {
      return segments[1];
    }
    if (segments.length >= 1 && MEETING_SCOPED_BASE_PATHS.has(segments[0])) {
      return segments[0];
    }
    return '';
  }, []);

  const isAdminPagePath = useCallback((pathname) => {
    const page = getMeetingScopedPage(pathname);
    return page === 'competition-admin'
      || page === 'banding-helper'
      || page === 'events-admin'
      || page === 'system-settings';
  }, [getMeetingScopedPage]);

  const isLiveTimetablePath = useCallback((pathname) => getMeetingScopedPage(pathname) === 'live-timetable', [getMeetingScopedPage]);

  const isNoSidebarPage = useCallback((pathname) => {
    return pathname === '/'
      || pathname === '/login'
      || pathname === '/signup'
      || pathname === '/forgot-password'
      || pathname === '/reset-password'
      || pathname === '/privacy-policy'
      || pathname === '/select-meeting'
      || getMeetingScopedPage(pathname) === 'draw-export-pdf'
      || pathname.startsWith('/auth/');
  }, [getMeetingScopedPage]);

  const isAuthStandalonePage = useMemo(
    () => location.pathname === '/login'
      || location.pathname === '/signup'
      || location.pathname === '/forgot-password'
      || location.pathname === '/reset-password',
    [location.pathname]
  );

  const isLandingPage = useMemo(
    () => location.pathname === '/',
    [location.pathname]
  );

  const isSelectMeetingPage = useMemo(
    () => location.pathname === '/select-meeting',
    [location.pathname]
  );

  const isPrivacyPolicyPage = useMemo(
    () => location.pathname === '/privacy-policy',
    [location.pathname]
  );

  const meetingSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawMeetingId = String(params.get('meetingId') || '').trim();
    const meetingId = Number.parseInt(rawMeetingId, 10);
    if (!Number.isFinite(meetingId) || meetingId <= 0) return '';
    return `?meetingId=${meetingId}`;
  }, [location.search]);

  const activeSlug = useMemo(() => {
    const segments = String(location.pathname || '').split('/').filter(Boolean);
    if (segments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(segments[1])) {
      return String(segments[0] || '').trim().toLowerCase();
    }
    return '';
  }, [location.pathname]);

  const withMeetingContext = useCallback((pathname) => {
    let nextPathname = pathname;
    const targetSegments = String(pathname || '').split('/').filter(Boolean);
    const targetIsMeetingScoped = targetSegments.length >= 1 && MEETING_SCOPED_BASE_PATHS.has(targetSegments[0]);

    if (activeSlug && targetIsMeetingScoped) {
      nextPathname = `/${activeSlug}/${targetSegments.join('/')}`;
    }

    return meetingSearch ? { pathname: nextPathname, search: meetingSearch } : nextPathname;
  }, [activeSlug, meetingSearch]);

  const missingMeetingContext = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const meetingId = Number.parseInt(String(params.get('meetingId') || '').trim(), 10);
    const hasMeetingId = Number.isFinite(meetingId) && meetingId > 0;

    const segments = String(location.pathname || '').split('/').filter(Boolean);
    const isCanonicalMeetingScoped = segments.length === 1 && MEETING_SCOPED_BASE_PATHS.has(segments[0]);
    const isFriendlyMeetingScoped = segments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(segments[1]);
    const requiresMeetingContext = isCanonicalMeetingScoped || isFriendlyMeetingScoped;
    const hasMeetingContext = hasMeetingId || isFriendlyMeetingScoped;

    return requiresMeetingContext && !hasMeetingContext;
  }, [location.pathname, location.search]);

  const getCurrentPageBaseTitle = useCallback(() => {
    const timetableHeading = document.getElementById('tabelLabel')?.textContent?.trim();
    if (timetableHeading) return timetableHeading;

    const heading = document.querySelector('main h1, h1');
    const headingText = heading?.textContent?.trim();
    if (headingText) return headingText;

    return competitionName || title || 'regatta';
  }, [competitionName, title]);

  const clearResultTitleFlash = useCallback(() => {
    if (titleFlashIntervalRef.current) {
      window.clearInterval(titleFlashIntervalRef.current);
      titleFlashIntervalRef.current = null;
    }
    if (titleFlashTimeoutRef.current) {
      window.clearTimeout(titleFlashTimeoutRef.current);
      titleFlashTimeoutRef.current = null;
    }
    if (titleFlashBaseTitleRef.current) {
      document.title = titleFlashBaseTitleRef.current;
    }
  }, []);

  const flashResultTitle = useCallback((message) => {
    const alertTitle = String(message || '').trim();
    if (!alertTitle) return;

    titleFlashBaseTitleRef.current = getCurrentPageBaseTitle();
    clearResultTitleFlash();
    const baseTitle = titleFlashBaseTitleRef.current || 'regatta';

    let showAlertTitle = true;
    document.title = alertTitle;

    titleFlashIntervalRef.current = window.setInterval(() => {
      document.title = showAlertTitle ? baseTitle : alertTitle;
      showAlertTitle = !showAlertTitle;
    }, 1000);

    titleFlashTimeoutRef.current = window.setTimeout(() => {
      clearResultTitleFlash();
    }, resultTitleFlashDurationMs);
  }, [clearResultTitleFlash, getCurrentPageBaseTitle, resultTitleFlashDurationMs]);

  const buildRaceResultFlashMessage = useCallback(async (raceid, winnerid) => {
    try {
      const token = getToken();
      const response = await fetch(`/api/timetable/${raceid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const race = await response.json();

      const winnerTitle = winnerid === 'a'
        ? (race?.laneA?.titleShort || race?.laneA?.title || '')
        : (winnerid === 'b' ? (race?.laneB?.titleShort || race?.laneB?.title || '') : '');
      const winnerLabel = String(winnerTitle || '').trim() || 'Race winner';
      const raceNo = race?.raceNo || raceid;

      return `${winnerLabel} won race ${raceNo}`;
    } catch (_error) {
      return `Race winner won race ${raceid}`;
    }
  }, [getToken]);

  const navGroups = useMemo(
    () => groupLinks({ isAdmin, isCommentary, isRegistration, isSponsorAdmin }),
    [isAdmin, isCommentary, isRegistration, isSponsorAdmin]
  );

  useEffect(() => {
    if (competitionName) {
      setTitle(competitionName);
      document.title = competitionName;
    }
  }, [competitionName]);

  useEffect(() => {
    titleFlashBaseTitleRef.current = getCurrentPageBaseTitle();
    document.title = titleFlashBaseTitleRef.current;
  }, [getCurrentPageBaseTitle, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      window.localStorage.setItem('app-sidebar-collapsed', String(sidebarCollapsed));
    } catch (_error) {
      // ignore storage errors in private modes
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const unsubscribe = subscribeToRealtime({
      onReceiveRaceResult: (_competitionId, raceid, winnerid) => {
        const path = window.location.pathname || '';
        if (isAdminPagePath(path) || isLiveTimetablePath(path)) return;
        buildRaceResultFlashMessage(raceid, winnerid).then((message) => {
          flashResultTitle(message);
        });
      }
    });

    return () => {
      unsubscribe();
      clearResultTitleFlash();
    };
  }, [buildRaceResultFlashMessage, clearResultTitleFlash, flashResultTitle, isAdminPagePath, isLiveTimetablePath]);

  const userName = user?.user_metadata?.display_name || user?.user_metadata?.full_name || user?.email || 'Profile';

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'is-sidebar-collapsed' : ''} ${isNoSidebarPage(location.pathname) ? 'no-sidebar' : ''}`}>
      {!isNoSidebarPage(location.pathname) && (
        <aside className={`app-sidebar ${mobileOpen ? 'is-open' : ''}`}>
          <div className="app-sidebar-header">
            <Link className="app-brand" to={withMeetingContext('/live-timetable')}>
              <span className="app-brand-mark">R</span>
              <span className="app-brand-title">regattamanager</span>
            </Link>
          </div>

          <nav className="app-sidebar-nav" aria-label="Primary">
            {navGroups.map((group) => (
              <section className="app-nav-group" key={group.title}>
                <h2 className="app-nav-group-title">{group.title}</h2>
                {group.links.map((item) => (
                  <NavLink
                    key={item.to}
                    to={withMeetingContext(item.to)}
                    exact={item.exact}
                    className="app-nav-link"
                    activeClassName="is-active"
                  >
                    {item.label}
                  </NavLink>
                ))}
              </section>
            ))}
          </nav>

          <button
            type="button"
            className="app-sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed(true)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <SidebarToggleIcon collapsed={false} />
          </button>
        </aside>
      )}

      {sidebarCollapsed && !isNoSidebarPage(location.pathname) && (
        <button
          type="button"
          className="app-sidebar-collapse-launcher"
          onClick={() => setSidebarCollapsed(false)}
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <SidebarToggleIcon collapsed />
        </button>
      )}

      {mobileOpen && <button className="app-overlay" onClick={() => setMobileOpen(false)} aria-label="Close menu" />}

      <div className="app-main-wrap">
        <header className="app-topbar">
          {!isNoSidebarPage(location.pathname) && (
            <button className="app-menu-toggle" onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle menu">
              <span />
              <span />
              <span />
            </button>
          )}

          {isLandingPage && (
            <Link className="app-topbar-brand" to="/" aria-label="RegattaManager home">
              <span className="app-topbar-brand-mark" aria-hidden="true">R</span>
              <span className="app-topbar-brand-title">regattamanager</span>
            </Link>
          )}

          {isAdmin && !isSelectMeetingPage && !isPrivacyPolicyPage && <MeetingSwitcher />}

          <div className="app-topbar-actions">
            {!user && !isNoSidebarPage(location.pathname) && (
              <Link className="app-topbar-link" to="/login">
                Official&apos;s Login
              </Link>
            )}

            {!user && isNoSidebarPage(location.pathname) && !isAuthStandalonePage && (
              <>
                <Link className="app-topbar-link app-topbar-link-ghost" to="/login">
                  Sign in
                </Link>
                <Link className="app-topbar-link app-topbar-link-primary" to="/signup">
                  Sign up
                </Link>
              </>
            )}

            {user && (
              <Dropdown isOpen={userDropdownOpen} toggle={() => setUserDropdownOpen(!userDropdownOpen)}>
                <DropdownToggle caret className="app-user-menu">
                  {userName}
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem header>
                    Email: {String(user?.email || '').trim() || '(unknown)'}
                  </DropdownItem>
                  <DropdownItem tag={Link} to="/login">Profile</DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={() => { setUserDropdownOpen(false); setTimeout(signOut, 50); }}>Logout</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </div>
        </header>

        <main className="app-content">
          {missingMeetingContext ? (
            <div style={{ padding: '1.5rem' }}>
              <div className="alert alert-warning" role="alert" style={{ maxWidth: '760px' }}>
                <h4 className="alert-heading">Missing Meeting in URL</h4>
                <p className="mb-0">
                  missing meeting in URL.
                </p>
                <p className="mb-0 mt-2">
                  This page requires a meeting context. Use a friendly URL like /sar26/live-timetable
                  or include ?meetingId=&lt;id&gt; in the URL.
                </p>
              </div>
            </div>
          ) : children}
        </main>
      </div>
    </div>
  );
}